import React, { useEffect, useMemo } from 'react';
import { NavigationProp, RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { translate } from '../../../services/translate';
import { useForm, useWatch } from 'react-hook-form';
import CustomButton from '../../../components/CustomButton';
import Card from '../../../components/Card';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { dispatchService, UnitCategoryType, VehicleChecklistMoment } from '../../../services/central-api/dispatch';
import { ActivityIndicator, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Tabs } from '../../../components/Tabs';
import { ScrollView } from 'react-native-web-hover';
import { SectionHeader } from '../../FormCommons';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import FormInput from '../../../components/FormInput';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import { makeSetStateHandler } from '../../../components/helpers/form';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import MyAppText from '../../../components/MyAppText';
import { WindowInformation } from '../../../services/window-information';
import FilterCheckboxList, { FilterListItem } from '../../../components/Filters/FilterCheckboxList';
import FilterRadioList from '../../../components/Filters/FilterRadioList';

interface UnitCategoryForm {
    name: string;
    type: UnitCategoryType;
    actingBodyId: number | null;
    hasPlate: boolean;
    generateOSE: boolean;
    startFuelLevel: boolean;
    endFuelLevel: boolean;
    checklist?: {
        [key in VehicleChecklistMoment]?: Partial<VehicleChecklistValues>;
    };
}

const defaultVehicleChecklistValues: VehicleChecklistValues = {
    fuel: false,
    oil: false,
    water: false,
    tires: false,
    horn: false,
    windshield_wiper: false,
    headlights: false,
    giroflex: false,
    siren: false,
    skewer: false,
    hydraulic_jack: false,
    wheel_wrench: false,
    fire_extinguisher: false,
    rearview_mirror: false,
    steering: false,
    clutch: false,
    foot_brake: false,
    parking_brake: false,
    engine_preheater: false,
    springs: false,
    shock_absorbers: false,
    engine_operation: false,
    general_leaks: false,
    battery: false,
    electrical_wires: false,
};

const VEHICLE_CHECKLIST_KEYS = Object.keys(defaultVehicleChecklistValues) as (keyof VehicleChecklistValues)[];

export default function UnitCategory() {
    const route = useRoute<RouteProp<DispatchParamList, 'UnitCategory'>>();
    const unitCategoryId = route.params?.id;
    const navigation = useNavigation<NavigationProp<DispatchParamList>>();
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();

    const { styles, theme } = useStyles(styleSheet);

    const { data, isLoading } = useQuery({
        queryKey: ['unitCategory', unitCategoryId],
        queryFn: () => dispatchService.getUnitCategoryById(unitCategoryId!),
        enabled: Boolean(unitCategoryId) && Boolean(authenticatedUser),
    });

    interface ChecklistRecordDto {
        id: number;
        checklistValue: keyof VehicleChecklistValues;
        moment: VehicleChecklistMoment;
        createdAt: string;
        updatedAt: string;
    }

    function convertChecklistArrayToFormValue(
        checklistArray?: ChecklistRecordDto[]
    ): UnitCategoryForm['checklist'] {
        const result: UnitCategoryForm['checklist'] = {
            before_departure: { ...defaultVehicleChecklistValues },
            during_service: { ...defaultVehicleChecklistValues },
            after_service: { ...defaultVehicleChecklistValues },
        };

        if (!checklistArray) {
            return result;
        }

        for (const record of checklistArray) {
            const momentValues = result[record.moment];
            if (momentValues) {
                momentValues[record.checklistValue] = true;
            }
        }

        return result;
    }

    useEffect(() => {
        if (!authenticatedUser) {
            return;
        }

        if (!unitCategoryId) {
            form.reset({
                ...defaultEmptyValues,
                ...(!(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_unit_category) && authenticatedUser.actingBodyUnit.actingBodyId ? {
                    actingBodyId: authenticatedUser.actingBodyUnit.actingBodyId
                } : {})
            });
        }

        if (!data) {
            return;
        }

        form.reset({
            actingBodyId: data.actingBodyId,
            generateOSE: data.generateOSE,
            hasPlate: data.hasPlate,
            name: data.name,
            type: data.type,
            startFuelLevel: data.startFuelLevel,
            endFuelLevel: data.endFuelLevel,
            checklist: convertChecklistArrayToFormValue(data.checklist)
        });

    }, [unitCategoryId, data, authenticatedUser]);

    const defaultEmptyValues: UnitCategoryForm = {
        name: '',
        type: UnitCategoryType.ON_FOOT,
        actingBodyId: null,
        hasPlate: false,
        generateOSE: false,
        startFuelLevel: false,
        endFuelLevel: false,
        checklist: {
            after_service: { ...defaultVehicleChecklistValues },
            during_service: { ...defaultVehicleChecklistValues },
            before_departure: { ...defaultVehicleChecklistValues },
        }
    };

    const form = useForm<UnitCategoryForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const checklistValue = useWatch({
        control: form.control,
        name: 'checklist',
    });

    const checklistItems: FilterListItem[] = useMemo(
        () => VEHICLE_CHECKLIST_KEYS.map((key, index) => ({
            label: translate(key),
            value: index,
        })),
        []
    );

    function getSelectedChecklistIndices(moment: VehicleChecklistMoment): number[] {
        const momentValues = checklistValue?.[moment];

        if (!momentValues) {
            return [];
        }

        return VEHICLE_CHECKLIST_KEYS.reduce<number[]>((acc, key, index) => {
            if (momentValues[key]) {
                acc.push(index);
            }
            return acc;
        }, []);
    }

    function handleChecklistChange(moment: VehicleChecklistMoment) {
        return (action: React.SetStateAction<number[]>) => {
            const currentIndices = getSelectedChecklistIndices(moment);
            const newIndices = typeof action === 'function'
                ? (action as (prev: number[]) => number[])(currentIndices)
                : action;

            const newMomentValues = VEHICLE_CHECKLIST_KEYS.reduce((acc, key, index) => {
                acc[key] = newIndices.includes(index);
                return acc;
            }, {} as VehicleChecklistValues);

            form.setValue('checklist', {
                ...checklistValue,
                [moment]: newMomentValues,
            }, {
                shouldValidate: true,
                shouldDirty: true,
            });
        };
    }

    async function formSubmit() {
        try {
            const data = form.getValues();

            if (unitCategoryId) {
                await dispatchService.updateUnitCategory({
                    id: unitCategoryId,
                    name: data.name,
                    type: data.type,
                    hasPlate: data.hasPlate,
                    generateOSE: data.generateOSE,
                    startFuelLevel: data.startFuelLevel,
                    endFuelLevel: data.endFuelLevel,
                    checklist: data.checklist
                });
            } else if (data.actingBodyId) {
                await dispatchService.createUnitCategory({
                    name: data.name,
                    actingBodyId: data.actingBodyId,
                    type: data.type,
                    hasPlate: data.hasPlate,
                    generateOSE: data.generateOSE,
                    startFuelLevel: data.startFuelLevel,
                    endFuelLevel: data.endFuelLevel,
                    checklist: data.checklist
                });
            }

            form.reset(defaultEmptyValues);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            navigation.canGoBack() ? navigation.goBack() : navigation.navigate('UnitCategories', { page: 0, limit: 25 });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <Container>
        <Header
            title={translate('registerUnitCategory')}
            goBackAction={() => {
                form.reset(defaultEmptyValues);
                navigation.canGoBack() ? navigation.goBack() : navigation.navigate('UnitCategories', { page: 0, limit: 25 });
            }}
            actions={
                <>
                    <CustomButton
                        text={translate('save')}
                        onPress={formSubmit}
                        disabled={!form.formState.isValid} />
                </>
            }
        />
        <Container>
            <Card>
                {(!!unitCategoryId && isLoading)
                    ? <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                    : <Tabs
                        tabs={[{
                            key: 'categoryInformation',
                            label: translate('categoryInformation'),
                        }]}
                    >
                        {({ selectedTab }) => {
                            if (selectedTab === 'categoryInformation') {
                                return <ScrollView contentContainerStyle={{ gap: 24 }}>
                                    <SectionHeader title={'actingBody'} viewZIndex={2}>
                                        <ActingBodiesDropdown
                                            includeAllOption={false}
                                            zIndex={5}
                                            disabled={!!unitCategoryId || !(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_category)}
                                            required={true}
                                            form={form}
                                            fieldName='actingBodyId'
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={{ gap: 8 }}
                                        />
                                    </SectionHeader>

                                    <SectionHeader title='category'>
                                        <FormInput
                                            label={translate('title')}
                                            required={true}
                                            form={form}
                                            fieldName='name'
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={{ gap: 8 }}
                                        />
                                    </SectionHeader>

                                    <SectionHeader title='type' required>
                                        <FilterRadioList
                                            clearValue={UnitCategoryType.ON_FOOT}
                                            items={[{
                                                label: translate('on_foot'),
                                                value: UnitCategoryType.ON_FOOT
                                            }, {
                                                label: translate('two_wheels'),
                                                value: UnitCategoryType.TWO_WHEELS
                                            }, {
                                                label: translate('four_wheels'),
                                                value: UnitCategoryType.FOUR_WHEELS
                                            }, {
                                                label: translate('other'),
                                                value: UnitCategoryType.OTHER
                                            }]}
                                            value={form.watch('type')}
                                            setValue={makeSetStateHandler(form, 'type')}
                                            label={translate('unitCategoryType')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            hideCount
                                            hideClearValue
                                        />
                                    </SectionHeader>

                                    <SectionHeader title='configuration'>
                                        <View style={{ flexDirection: 'column', gap: 8 }}>
                                            <FilterCheckBox
                                                label={translate('hasPlate')}
                                                onChange={(value) => {
                                                    form.setValue('hasPlate', value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                value={form.watch('hasPlate')}
                                                labelStyle={styles.checkboxLabelStyle}
                                            />

                                            <FilterCheckBox
                                                label={translate('generateOSE')}
                                                onChange={(value) => {
                                                    form.setValue('generateOSE', value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                value={form.watch('generateOSE')}
                                                labelStyle={styles.checkboxLabelStyle}
                                            />
                                        </View>
                                    </SectionHeader>

                                    <SectionHeader title='fuelLevel'>
                                        <View style={{ flexDirection: 'row', gap: 80 }}>
                                            <FilterCheckBox
                                                label={translate('beforeStartingWork')}
                                                onChange={(value) => {
                                                    form.setValue('startFuelLevel', value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                value={form.watch('startFuelLevel')}
                                                labelStyle={styles.checkboxLabelStyle}
                                            />

                                            <FilterCheckBox
                                                label={translate('afterStartingWork')}
                                                onChange={(value) => {
                                                    form.setValue('endFuelLevel', value, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }}
                                                value={form.watch('endFuelLevel')}
                                                labelStyle={styles.checkboxLabelStyle}
                                            />
                                        </View>
                                    </SectionHeader>

                                    <SectionHeader title='vehicleChecklist'>
                                        <View style={{ flexDirection: 'column', gap: 16 }}>
                                            <View>
                                                <MyAppText style={styles.vehicleChecklistDescription}>{translate('vehicleChecklistDescription1')}</MyAppText>
                                                <MyAppText style={styles.vehicleChecklistDescription}>{translate('vehicleChecklistDescription2')}</MyAppText>
                                            </View>
                                            <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 24 }}>
                                                {Object.values(VehicleChecklistMoment).map((moment) => (
                                                    <View key={moment} style={{ flex: 1 }}>
                                                        <FilterCheckboxList
                                                            label={translate(moment)}
                                                            items={checklistItems}
                                                            value={getSelectedChecklistIndices(moment)}
                                                            setValue={handleChecklistChange(moment)}
                                                        />
                                                    </View>
                                                ))}
                                            </View>
                                        </View>
                                    </SectionHeader>
                                </ScrollView>;
                            }
                        }}
                    </Tabs>
                }
            </Card>
        </Container>
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    checkboxLabelStyle: {
        color: theme.colors.neutral['400'],
        fontWeight: '400',
        fontSize: 14,
    },
    vehicleChecklistDescription: {
        color: theme.colors.neutral['400'],
        fontWeight: '400',
        fontSize: 16,
    }
}));
